import { useEffect, useState } from 'react';
import { api, type AdminStats, type CountryRow } from '@/api/client';
import { Card, ErrorBox, Loading, PageTitle } from '@/components/ui';

/** ISO-3166 alpha-2 → flag emoji via regional-indicator codepoints. */
function flagEmoji(code: string | null): string {
  if (!code || code.length !== 2) return '🏳️';
  const base = 0x1f1e6;
  const cc = code.toUpperCase();
  return String.fromCodePoint(
    base + (cc.charCodeAt(0) - 65),
    base + (cc.charCodeAt(1) - 65),
  );
}

export function DashboardPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [countries, setCountries] = useState<CountryRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.stats().then(setStats).catch((e) => setErr(String(e)));
    api.countries().then((r) => setCountries(r.items)).catch(() => setCountries([]));
  }, []);

  if (err) return <ErrorBox text={err} />;
  if (!stats) return <Loading />;

  const countryTotal = (countries ?? []).reduce((s, c) => s + c.count, 0);

  return (
    <div>
      <PageTitle title="Dashboard" />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <Card title="PLAYERS" value={stats.users.toLocaleString()} accent="var(--c-cyan)" />
        <Card title="ADMINS" value={stats.admins.toLocaleString()} accent="var(--c-purple)" />
        <Card title="BANNED" value={stats.banned.toLocaleString()} accent="var(--c-red)" />
        <Card title="MATCHES" value={stats.totalMatches.toLocaleString()} accent="var(--c-yellow)" />
        <Card title="MATCHES · 24H" value={stats.matchesLast24h.toLocaleString()} accent="var(--c-blue)" />
        <Card
          title="CONFIG WARMED"
          value={
            stats.settingsLastWarmedAt > 0
              ? `${Math.round((Date.now() - stats.settingsLastWarmedAt) / 1000)}s ago`
              : '—'
          }
        />
      </div>

      <div className="card" style={{ padding: 16, marginTop: 18 }}>
        <div className="tag" style={{ marginBottom: 8 }}>TOP 5 BY MMR</div>
        {stats.topPlayers.length === 0 ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 13 }}>No players yet.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Player</th>
                <th>Rank</th>
                <th style={{ textAlign: 'right' }}>MMR</th>
              </tr>
            </thead>
            <tbody>
              {stats.topPlayers.map((p, i) => (
                <tr key={p.id} style={{ cursor: 'default' }}>
                  <td className="mono" style={{ color: 'var(--c-yellow)' }}>
                    {i + 1}
                  </td>
                  <td style={{ fontWeight: 600 }}>{p.displayName}</td>
                  <td>
                    <span className="tag" style={{ fontSize: 9 }}>
                      {p.rank.toUpperCase()}
                    </span>
                  </td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--c-cyan)' }}>
                    {p.mmr}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginTop: 18 }}>
        <div className="tag" style={{ marginBottom: 8 }}>PLAYERS BY COUNTRY</div>
        {countries == null ? (
          <Loading />
        ) : countries.length === 0 ? (
          <div style={{ color: 'var(--ink-muted)', fontSize: 13 }}>No geo data yet.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Country</th>
                <th style={{ textAlign: 'right' }}>Players</th>
                <th style={{ width: 90, textAlign: 'right' }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c, i) => {
                const pct = countryTotal > 0 ? (c.count / countryTotal) * 100 : 0;
                return (
                  <tr key={c.country ?? '—'} style={{ cursor: 'default' }}>
                    <td className="mono" style={{ color: 'var(--c-yellow)' }}>
                      {i + 1}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {c.country ? `${flagEmoji(c.country)} ${c.country}` : 'Unknown'}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', color: 'var(--c-cyan)' }}>
                      {c.count.toLocaleString()}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', color: 'var(--ink-muted)' }}>
                      {pct.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
